import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ExternalLink, Globe, MapPin } from 'lucide-react';
import type { PostSummary, PublicAuthor } from '@shared/types';
import { PostCard } from '@/components/PostCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ShareMenu } from '@/components/ShareMenu';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { useSession } from '@/lib/session';
import { cn, initials } from '@/lib/utils';
import { t, n } from '@/lib/i18n';

/** Serves both /@handle on the platform and /about on an author's own domain. */
export function AuthorPage() {
  const params = useParams<{ handle?: string }>();
  const { site } = useSession();
  const tenant = site?.tenant ?? null;
  const handle = params.handle?.replace(/^@/, '') ?? tenant?.handle;

  const [author, setAuthor] = useState<PublicAuthor | null>(tenant);
  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!handle) return;
    const controller = new AbortController();
    setLoading(true);

    Promise.all([
      api.author(handle, controller.signal),
      api.feed({ author: handle, perPage: 30 }, controller.signal),
    ])
      .then(([authorResult, feedResult]) => {
        setAuthor(authorResult.author);
        setPosts(feedResult.items);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [handle]);

  if (loading && !author) {
    return (
      <div className="container max-w-3xl space-y-4 py-12">
        <Skeleton className="size-20 rounded-full" />
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-4 w-full" />
      </div>
    );
  }

  if (!author) {
    return (
      <div className="container max-w-2xl py-24 text-center">
        <h1 className="np text-3xl font-semibold">{t('लेखक भेटिएन')}</h1>
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-12">
      <header className="flex flex-wrap items-start gap-6">
        <Avatar className="size-20">
          {author.avatarUrl ? <AvatarImage src={author.avatarUrl} alt={author.displayName} /> : null}
          <AvatarFallback className="text-xl">
            {initials(author.penName || author.displayName)}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="np text-3xl font-semibold">{author.penName || author.displayName}</h1>
              <p className="text-sm text-muted-foreground">@{author.handle}</p>
            </div>
            {/* Their site, not this mirror of it — see canonicalPostUrl. */}
            <ShareMenu
              target={{ url: author.siteUrl, title: author.penName || author.displayName }}
              variant="ghost"
              iconOnly
            />
          </div>

          {author.bio && <p className="np mt-3 leading-relaxed">{author.bio}</p>}

          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {author.location && (
              <span className="np inline-flex items-center gap-1">
                <MapPin className="size-3.5" /> {author.location}
              </span>
            )}
            {author.websiteUrl && (
              <a
                href={author.websiteUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                <ExternalLink className="size-3.5" /> {new URL(author.websiteUrl).hostname}
              </a>
            )}
            {/*
              Their own address, shown only on the platform — on their own site
              you are already looking at it. When a custom domain is active,
              siteUrl *is* that domain (see toPublicAuthor), so this needs no
              branch: the writer who has one gets their name in front of readers,
              which is the point of having attached it.
            */}
            {!tenant && (
              <a
                href={author.siteUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:text-foreground"
                title={author.customDomain ? t('आफ्नै डोमेन') : undefined}
              >
                <Globe className={cn('size-3.5', author.customDomain && 'text-primary')} />
                {new URL(author.siteUrl).hostname}
              </a>
            )}
            {author.postCount !== undefined && (
              <span className="np tnum">{t('{count} रचना', { count: n(author.postCount) })}</span>
            )}
          </div>
        </div>
      </header>

      <Separator className="my-8" />

      {posts.length === 0 ? (
        <p className="np py-12 text-center text-muted-foreground">{t('अझै कुनै प्रकाशित रचना छैन।')}</p>
      ) : (
        <div className="divide-y">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} showAuthor={false} />
          ))}
        </div>
      )}
    </div>
  );
}
